import { matchPath } from 'react-router-dom';

import { combineEpics } from 'redux-observable';
import { EMPTY, Observable } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { NetworkRequestState, selectIsUserLoggedIn } from '@bolteu/redux-session';

import { AppRoutes } from 'root/routing/AppRoutes';
import { selectAccountRequestState, selectSelectedProvider } from 'rdx/account';
import { GlobalAppActions, routeChangeAction } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { GlobalAppState } from 'rdx/state';

import { MixpanelEventType } from '@provider-portal/common/services/MixpanelService';
import { getMixpanelExtraParams } from '@provider-portal/common/utils/getMixpanelExtraParams';

type AnalyticsDependencies = {
  mixpanelService: Pick<AppServices['mixpanelService'], 'trackAccountEvent'>;
};

const trackMixpanelEventsOnRouteChangeEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { mixpanelService }: AnalyticsDependencies,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(routeChangeAction)),
    // The initial route change fires before session rehydration and account load; wait so providerId is tracked.
    // switchMap on purpose: only the latest pending route is tracked, as before ConnectedRouter mounted
    switchMap((action) =>
      state$.pipe(
        first(
          (state) =>
            !!state.session._persist?.rehydrated &&
            (selectAccountRequestState(state) === NetworkRequestState.success ||
              !selectIsUserLoggedIn(state)),
        ),
        map((state) => [action, state] as const),
      ),
    ),
    switchMap(([action, state]) => {
      const { pathname, search, state: locationState } = action.payload.location;

      if (pathname) {
        Object.values(AppRoutes).forEach((path) => {
          const match = matchPath(pathname, {
            path,
            exact: true,
            strict: true,
          });

          if (match) {
            const providerId = selectSelectedProvider(state)?.providerId;
            const extraParams = getMixpanelExtraParams('', search);
            mixpanelService.trackAccountEvent(MixpanelEventType.NAVIGATION_PATH_CHANGE, {
              ...(providerId ? { providerId } : {}),
              path: match.path,
              state: locationState,
              ...match.params,
              ...extraParams,
            });
          }
        });
      }

      return EMPTY;
    }),
  );
};

export const analyticEpic = combineEpics(trackMixpanelEventsOnRouteChangeEpic);
